'use client'

;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();import { usePathname, useRouter } from 'next/navigation'
import { useCallback, useEffect } from 'react'

import { useCalendly } from '@/components/integrations/calendly/calendly-provider'
import { useIntercom } from '@/components/integrations/intercom/intercom-provider'
import { trackCta } from '@/lib/analytics/track-cta'

type LinkActionListenerProps = {
  cleanupQuery?: boolean
}

type LinkAction = 'request-demo' | 'chat'

/** The Messenger panel, as Intercom names it. Measured on production, 2026-10-02. */
const MESSENGER_FRAME = 'iframe[name="intercom-messenger-frame"]'

/** The `location` a click inside the chat carries onto `demo_opened` and `cta_clicked`. */
const CHAT_LOCATION = 'intercom-chat'

/** What a link is asking to open, if anything. `demo=1` is the older spelling of `request-demo`. */
function requestedAction(params: URLSearchParams): LinkAction | null {
  const action = params.get('action')
  if (action === 'chat') {
    return 'chat'
  }
  return action === 'request-demo' || params.get('demo') === '1' ? 'request-demo' : null
}

/**
 * A click the page may answer itself. A modified click means "open this elsewhere" and is left to
 * the browser, as is anything another handler has already answered.
 */
function isPlainClick(event: MouseEvent): boolean {
  return !event.defaultPrevented && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey
}

/**
 * The link a click landed on, resolved against the page. Inside the Messenger frame this works the
 * same way: its document is `about:blank`, which takes the page's base URL, so `href` is absolute.
 */
function clickedLink(event: MouseEvent): { anchor: HTMLAnchorElement; url: URL } | null {
  const anchor = (event.target as Element | null)?.closest?.('a')
  if (!anchor?.href) {
    return null
  }
  return { anchor, url: new URL(anchor.href, window.location.href) }
}

/**
 * Whether an anchor inside the Messenger is a link its author wrote, as opposed to Intercom's own
 * UI. Content links are pasted as full URLs. A fragment or a relative `href` on an anchor Intercom
 * renders for itself would resolve to this origin too — the frame takes the page's base URL — and
 * answering it would hijack the Messenger.
 */
function isAuthoredLink(anchor: HTMLAnchorElement): boolean {
  return /^https?:\/\//i.test(anchor.getAttribute('href') ?? '')
}

/**
 * The URL to push for a link clicked in the chat: path, query and hash, minus the campaign.
 *
 * Every link in Intercom carries `utm_*` (Creatives ADR 0075), and `AttributionCapture` would make
 * that the last touch (ADR-0011). The chat is a step inside the visit, not a new marketing touch:
 * the ad that brought the visitor keeps the sign-up it earned. Every other parameter (`plan`,
 * `ref`, `lang`, …) is the link's own and stays.
 */
function withoutCampaign(url: URL): string {
  const params = new URLSearchParams(url.search)
  for (const key of Array.from(params.keys())) {
    if (key.startsWith('utm_')) {
      params.delete(key)
    }
  }
  const query = params.toString()
  return `${url.pathname}${query ? `?${query}` : ''}${url.hash}`
}

/**
 * The parameters an arriving link spends on this listener: what to open, and who the planner is for.
 */
const ACTION_PARAMS = ['action', 'demo', 'id', 'user'] as const

/**
 * The URL left in the address bar once an arriving link has been answered: path, query and hash,
 * minus the parameters this listener consumed — so a reload does not reopen the dialog, and the
 * person's id does not linger. Everything else stays, the campaign above all: PostHog starts when
 * the browser goes idle (`src/lib/analytics/posthog.ts`) and reads its `utm_*` and click ids from
 * the URL it finds then, so a campaign link that also asks for the chat or the planner would
 * otherwise reach PostHog as a direct visit.
 */
function withoutActionParams(url: URL): string {
  const params = new URLSearchParams(url.search)
  for (const key of ACTION_PARAMS) {
    params.delete(key)
  }
  const query = params.toString()
  return `${url.pathname}${query ? `?${query}` : ''}${url.hash}`
}

/**
 * Opens the demo planner or the chat when a link asks for it — on the page, and inside the Intercom
 * Messenger.
 *
 * Content cannot call `useCalendly` or `useIntercom` — an article is MDX, not a client component —
 * so it asks in the URL instead: `?action=request-demo` or `?action=chat`. Two things then have to
 * happen. A visitor arriving on such a URL (a campaign link, a shared one, a new tab) gets the
 * dialog on load, and the action's own parameters are dropped so a reload does not reopen it; the
 * rest of the query, the campaign included, stays. A visitor clicking such a link inside an article
 * gets the dialog then and there — travelling to another page to be shown a popup loses both the
 * article and the point of the link.
 *
 * `?demo=1` keeps working alongside `?action=request-demo`, because links out in the world already
 * carry it, optionally with a `user` to prefill the planner from.
 *
 * The Messenger renders its own anchors, with `target="_blank"`, inside a same-origin iframe, where
 * neither `next/link` nor the page's click listener can see them. So the same handler is attached to
 * that frame's document too, and a same-origin link there is answered the way a `next/link` would:
 * the panel closes, `?action=` opens the dialog in place, anything else is a client-side
 * `router.push`. Spec: `docs/specs/2026-10-02-intercom-messenger-links.md`.
 */
export function LinkActionListener({ cleanupQuery = true }: LinkActionListenerProps) {
  const { open } = useCalendly()
  const { open: openChat, hide: hideChat } = useIntercom()
  const pathname = usePathname()
  const router = useRouter()

  const perform = useCallback(
    async (action: LinkAction, params: URLSearchParams, location?: string) => {
      if (action === 'chat') {
        openChat()
        return
      }

      // Campaign links name the person they were sent to, so the planner can fill itself in.
      const user = params.get('id') ?? params.get('user')
      const prefill = user
        ? await fetch(`/api/request-demo?user=${encodeURIComponent(user)}`)
            .then((response) => (response.ok ? response.json() : null))
            .catch(() => null)
        : null

      open({ user, prefill: (prefill as { name?: string; email?: string } | null) ?? undefined, location })
    },
    [open, openChat],
  )

  // Arriving on a URL that asks for one.
  useEffect(() => {
    if (!pathname || typeof window === 'undefined') {
      return
    }

    const params = new URLSearchParams(window.location.search)
    const action = requestedAction(params)
    if (!action) {
      return
    }

    if (cleanupQuery) {
      // `history` rather than `router.replace`: this is only meant to tidy the address bar, and a
      // router navigation on the first render is dropped before the page has hydrated.
      window.history.replaceState(null, '', withoutActionParams(new URL(window.location.href)))
    }
    void perform(action, params)
  }, [cleanupQuery, pathname, perform])

  // Clicking one, anywhere on the page.
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      // A modified click means "open this elsewhere"; let it navigate, and the effect above will
      // open the dialog on the page it lands on.
      if (!isPlainClick(event)) {
        return
      }

      const link = clickedLink(event)
      if (!link || link.url.origin !== window.location.origin) {
        return
      }

      const action = requestedAction(link.url.searchParams)
      if (!action) {
        return
      }

      event.preventDefault()
      event.stopPropagation()
      void perform(action, link.url.searchParams)
    }

    // Capture, because React listens at the app root: by the time a click bubbles up to the
    // document, `next/link` has already started navigating and preventing it comes too late.
    document.addEventListener('click', onClick, true)
    return () => document.removeEventListener('click', onClick, true)
  }, [perform])

  // Clicking one inside the Messenger.
  useEffect(() => {
    const onChatClick = (event: MouseEvent) => {
      if (!isPlainClick(event)) {
        return
      }

      // Anything not on this origin keeps Intercom's new tab: workspace URLs, the help centre,
      // Calendly, `mailto:`. So do downloads. On staging the chat's links point at production and
      // therefore open a new tab too, which is correct.
      const link = clickedLink(event)
      if (
        !link ||
        !isAuthoredLink(link.anchor) ||
        link.url.origin !== window.location.origin ||
        link.anchor.hasAttribute('download')
      ) {
        return
      }

      event.preventDefault()
      event.stopPropagation()

      const action = requestedAction(link.url.searchParams)
      if (action === 'chat') {
        // Already there.
        return
      }

      // Close the panel first: on a phone it is full-screen, and the planner or the page would
      // otherwise open underneath it.
      hideChat()

      if (action) {
        void perform(action, link.url.searchParams, CHAT_LOCATION)
        return
      }

      // The step key Creatives puts in `utm_content` (ADR 0075) names the link better than its text.
      trackCta(CHAT_LOCATION, link.url.searchParams.get('utm_content') ?? link.anchor.textContent?.trim() ?? '')
      router.push(withoutCampaign(link.url))
    }

    // Every listener this effect adds — on frame elements and on the documents inside them — hangs
    // off one signal, so the cleanup is a single `abort()` and a re-run of the effect can never leave
    // a stale handler behind. Attaching is idempotent (the DOM ignores an identical listener), so a
    // document is simply attached to on every chance: when its frame appears, and on every `load` of
    // that frame. The `load` matters twice over. Intercom may give the frame a new document, and it
    // may rewrite the existing one with `document.open()`, which keeps the `Document` object and
    // erases every listener on it.
    const controller = new AbortController()
    const { signal } = controller

    const attach = (frame: HTMLIFrameElement) => {
      let doc: Document | null = null
      try {
        doc = frame.contentDocument
      } catch {
        // Cross-origin. Nothing is attached; links fall back to Intercom's new tab, and the on-load
        // half above handles `?action=` when that tab lands.
        return
      }
      doc?.addEventListener('click', onChatClick, { capture: true, signal })
    }

    const watched = new WeakSet<HTMLIFrameElement>()
    const watch = (frame: HTMLIFrameElement) => {
      if (!watched.has(frame)) {
        watched.add(frame)
        frame.addEventListener('load', () => attach(frame), { signal })
      }
      attach(frame)
    }

    // A frame that is already there — Intercom booted before this effect, or the effect re-ran.
    document.querySelectorAll<HTMLIFrameElement>(MESSENGER_FRAME).forEach(watch)

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) {
            return
          }
          if (node.matches(MESSENGER_FRAME)) {
            watch(node as HTMLIFrameElement)
          }
          node.querySelectorAll<HTMLIFrameElement>(MESSENGER_FRAME).forEach(watch)
        })
      }
    })
    observer.observe(document.body, { childList: true, subtree: true })

    return () => {
      observer.disconnect()
      controller.abort()
    }
  }, [hideChat, perform, router])

  return null
}
